import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { defaultBrandKit } from "@veyso/core";
import { AppShell } from "../components/AppShell.js";
import { NewProjectCrumb } from "../components/NewProjectCrumb.js";
import { ErrorNote, Field } from "../components/ui.js";
import { ApiError, api } from "../api.js";
import { StylePicker } from "./StylePicker.js";
import { LengthPicker } from "./LengthPicker.js";
import { SiteBrandPreview, type SiteBrand } from "./SiteBrandPreview.js";
import { SiteFallback } from "./SiteFallback.js";
import { AiSummary } from "./AiSummary.js";
import { LookChoice } from "./LookChoice.js";
import {
  canLeaveAbout,
  emptySite,
  hostOf,
  recapLines,
  siteFailure,
  siteOwnLook,
  startRequest,
  wizardSteps,
  type AboutState,
  type Look,
  type LookColors,
  type SiteFailure,
  type SiteLook,
  type Summary,
  type WizardStep,
} from "./wizard.js";

type SiteState =
  | { kind: "idle" }
  | { kind: "reading" }
  | { kind: "read"; brand: SiteBrand }
  | { kind: "failed"; failure: SiteFailure };

type AiState = null | { phase: "looking"; host: string } | { phase: "ready"; host: string; summary: Summary };

/**
 * Making a launch video on purpose, in three steps: what it is about, its
 * style, its length.
 *
 * One step at a time, with everything kept as you move between them, and the
 * film started only from the last — so choosing a style is never one click
 * away from spending money. The steps live in this component, not the address
 * bar: a reload starts again at About, as the single form always did.
 *
 * A site that cannot be read says why and offers a way on — an AI summary of
 * the company, with its logo and colours chosen separately, or the customer's
 * own words. Nothing waits here once the film is started: the project is
 * created and the page leaves for it.
 */
export const NewLaunchVideo: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<WizardStep>("about");
  const [source, setSource] = useState<"website" | "topic">("website");
  const [website, setWebsite] = useState("");
  const [name, setName] = useState("");
  const [topic, setTopic] = useState("");
  const [topicNotice, setTopicNotice] = useState<string | null>(null);
  const [site, setSite] = useState<SiteState>({ kind: "idle" });
  const [fallbackChoice, setFallbackChoice] = useState<"ai" | "describe">("ai");
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [ai, setAi] = useState<AiState>(null);
  const [look, setLook] = useState<Look | null>(null);
  const [siteLook, setSiteLook] = useState<SiteLook>(siteOwnLook);
  const [kitColors, setKitColors] = useState<LookColors>(defaultBrandKit.colors);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [seconds, setSeconds] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The address the preview was read for, so the same one is not read twice. */
  const readFor = useRef("");

  // The kit's colours: what an upload starts from, and the accent a described film is shown in.
  useEffect(() => {
    let live = true;
    api
      .settings()
      .then((settings) => live && setKitColors(settings.brandKit.colors))
      .catch(() => {
        // The defaults are a fine starting point; the colours are editable.
      });
    return () => {
      live = false;
    };
  }, []);

  /** Everything learned about the old address belongs to the old address. */
  const forgetSite = () => {
    readFor.current = "";
    setSite({ kind: "idle" });
    setAi(null);
    setLook(null);
    setSiteLook(siteOwnLook);
    setLookupError(null);
    setFallbackChoice("ai");
  };

  /** The site's logo and colours, read when the address is finished being typed. */
  const readBrand = async () => {
    const address = website.trim();
    if (address.length <= 2 || address === readFor.current) return;
    readFor.current = address;
    setAi(null);
    setLook(null);
    setSiteLook(siteOwnLook);
    setSite({ kind: "reading" });
    try {
      const read = await api.brandFromWebsite(address);
      if (readFor.current !== address) return;
      setSite({ kind: "read", brand: read });
    } catch (cause) {
      if (readFor.current === address) setSite({ kind: "failed", failure: siteFailure(cause, address) });
    }
  };

  /** A read site with no words on it gets the same way on as one that refused us. */
  const noText = site.kind === "read" && !site.brand.foundText ? emptySite(website) : null;
  const failure = site.kind === "failed" ? site.failure : noText;

  const describeInstead = (notice: string | null) => {
    if (!name.trim() && site.kind === "read" && site.brand.name) setName(site.brand.name);
    setTopicNotice(notice);
    setSource("topic");
  };

  const lookUp = async () => {
    const address = website.trim();
    const host = hostOf(address);
    setLookupError(null);
    setAi({ phase: "looking", host });
    try {
      const answer = await api.lookupSite(address);
      if (readFor.current !== address) return;
      if (answer.found) {
        setAi({
          phase: "ready",
          host,
          summary: { name: answer.name, summary: answer.summary, sources: answer.sources },
        });
      } else {
        setAi(null);
        describeInstead(`We couldn't find enough about ${host}. Describe it yourself instead.`);
      }
    } catch (cause) {
      if (readFor.current !== address) return;
      setAi(null);
      setLookupError((cause as Error).message);
    }
  };

  const about: AboutState = {
    source,
    website,
    // A usable read only: a site with no words on it, or one whose AI lookup is
    // still running, must not let the customer leave About with a plain-site film.
    siteRead: site.kind === "read" && site.brand.foundText && ai === null,
    summary: ai?.phase === "ready" ? ai.summary : null,
    look,
    siteLook,
    name,
    topic,
  };
  const choice = { templateId, seconds };

  /** The accent the film will use, so the style strips show it. */
  const accent =
    source === "website" && site.kind === "read" && !about.summary
      ? (siteLook.colors ?? site.brand.colors).accent
      : about.summary && look?.kind === "upload"
        ? look.colors.accent
        : kitColors.accent;

  const start = async () => {
    setError(null);
    setBusy(true);
    try {
      const started = await api.startLaunchVideo(startRequest(about, choice));
      navigate(`/projects/${started.projectId}`, { replace: true });
    } catch (cause) {
      setError(cause instanceof ApiError || cause instanceof Error ? cause.message : "That didn't work. Try again.");
      setBusy(false);
    }
  };

  const index = wizardSteps.findIndex((one) => one.key === step);

  return (
    <AppShell breadcrumb={<NewProjectCrumb here="Launch video" />}>
      <div className="mx-auto flex w-full max-w-[680px] flex-col gap-6 px-4 py-8">
        <div>
          <h1 className="text-[26px] font-semibold text-ink-100">Launch video</h1>
          <p className="mt-1 text-[13px] leading-[1.6] text-ink-300">
            A motion graphic about your product — no recording needed. We write it, draw it and render it; it takes
            about ten minutes and you do not have to wait here.
          </p>
        </div>

        <ol className="flex flex-wrap gap-1.5" aria-label="Steps">
          {wizardSteps.map((one, i) => (
            <li
              key={one.key}
              aria-current={one.key === step ? "step" : undefined}
              className={`rounded-full px-3 py-1 text-[12px] font-medium ${
                one.key === step ? "bg-ink-100 text-bay-850" : i < index ? "bg-bay-800 text-ink-100" : "text-ink-400"
              }`}
            >
              {i + 1} · {one.label}
            </li>
          ))}
        </ol>

        {step === "about" ? (
          <section className="panel flex flex-col gap-4 p-5">
            <h2 className="text-[13px] font-semibold text-ink-100">What it is about</h2>

            {/* Two ways in, and only one at a time. A form offering both invites
                filling in both, and then something has to decide which to
                believe. */}
            <div className="inline-flex w-fit overflow-hidden rounded-[7px] border border-[var(--edge)]">
              {(["website", "topic"] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => {
                    setSource(option);
                    setTopicNotice(null);
                  }}
                  className={`px-3.5 py-1.5 text-[12.5px] font-medium transition-colors ${
                    source === option ? "bg-ink-100 text-bay-850" : "text-ink-300 hover:text-ink-100"
                  }`}
                >
                  {option === "website" ? "From my website" : "Describe it myself"}
                </button>
              ))}
            </div>

            {source === "website" ? (
              <>
                <Field label="Website">
                  <input
                    className="input w-full"
                    placeholder="acme.com"
                    value={website}
                    disabled={ai?.phase === "looking"}
                    onChange={(event) => {
                      setWebsite(event.target.value);
                      if (event.target.value.trim() !== readFor.current) forgetSite();
                    }}
                    onBlur={() => void readBrand()}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") void readBrand();
                    }}
                  />
                </Field>

                {ai ? (
                  <>
                    <AiSummary state={ai} onChange={(summary) => ai.phase === "ready" && setAi({ ...ai, summary })} />
                    {ai.phase === "ready" ? <LookChoice look={look} kitColors={kitColors} onChange={setLook} /> : null}
                  </>
                ) : (
                  <>
                    <p className="text-[11.5px] leading-[1.5] text-ink-400">
                      We read the page itself, not its meta description — that tag is written for search engines and is
                      as often a keyword list as a sentence. The film uses this site's logo and colours unless you
                      change them below, for this video only.
                    </p>
                    {site.kind === "reading" ? (
                      <SiteBrandPreview state={{ kind: "reading" }} look={siteLook} onLookChange={setSiteLook} />
                    ) : null}
                    {site.kind === "read" ? (
                      <SiteBrandPreview
                        state={{ kind: "read", brand: site.brand }}
                        look={siteLook}
                        onLookChange={setSiteLook}
                      />
                    ) : null}
                    {failure ? (
                      <SiteFallback
                        failure={failure}
                        choice={fallbackChoice}
                        onChoice={setFallbackChoice}
                        error={lookupError}
                        onContinue={() => (fallbackChoice === "ai" ? void lookUp() : describeInstead(null))}
                      />
                    ) : null}
                  </>
                )}
              </>
            ) : (
              <>
                {topicNotice ? <p className="text-[12px] text-signal-warn">{topicNotice}</p> : null}
                <Field label="Product name">
                  <input
                    className="input w-full"
                    placeholder="Acme"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                  />
                </Field>
                <Field label="What it does">
                  <textarea
                    className="input min-h-[96px] w-full resize-y"
                    placeholder="Acme pays your invoices the day they arrive, so nobody chases suppliers by hand."
                    value={topic}
                    onChange={(event) => setTopic(event.target.value)}
                  />
                </Field>
                <p className="text-[11.5px] leading-[1.5] text-ink-400">
                  A sentence or two is enough. Only claims you make here can end up in the film — nothing is invented.
                  Your logo and colours come from the brand kit.
                </p>
              </>
            )}
          </section>
        ) : null}

        {step === "style" ? (
          <section className="panel flex flex-col gap-4 p-5">
            <h2 className="text-[13px] font-semibold text-ink-100">Style</h2>
            <StylePicker value={templateId} onChange={setTemplateId} disabled={busy} brandAccent={accent} />
          </section>
        ) : null}

        {step === "length" ? (
          <>
            <section className="panel flex flex-col gap-4 p-5">
              <h2 className="text-[13px] font-semibold text-ink-100">Length</h2>
              <LengthPicker value={seconds} onChange={setSeconds} templateId={templateId} disabled={busy} />
            </section>
            <section className="panel flex flex-col gap-3 p-5">
              <h2 className="text-[13px] font-semibold text-ink-100">Ready to make</h2>
              <dl className="flex flex-col divide-y divide-[var(--edge)]">
                {recapLines(about, choice).map((line) => (
                  <div key={line.label} className="flex items-center gap-3 py-2 text-[12.5px]">
                    <dt className="w-16 shrink-0 text-ink-400">{line.label}</dt>
                    <dd className="min-w-0 flex-1 truncate text-ink-100">{line.value}</dd>
                    {line.step !== "length" ? (
                      <button
                        type="button"
                        className="text-[12px] font-medium text-ember-600 hover:underline"
                        disabled={busy}
                        onClick={() => setStep(line.step)}
                      >
                        Change
                      </button>
                    ) : null}
                  </div>
                ))}
              </dl>
            </section>
          </>
        ) : null}

        {error ? <ErrorNote>{error}</ErrorNote> : null}

        <div className="flex flex-wrap gap-2">
          {step === "about" ? (
            <>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!canLeaveAbout(about)}
                onClick={() => setStep("style")}
              >
                Next: Style
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => navigate("/projects/new")}>
                Back
              </button>
            </>
          ) : step === "style" ? (
            <>
              <button type="button" className="btn btn-primary" onClick={() => setStep("length")}>
                Next: Length
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setStep("about")}>
                ← About
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void start()}>
                {busy ? "Starting…" : "Make the video"}
              </button>
              <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setStep("style")}>
                ← Style
              </button>
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
};
